/**
 * Device capability detection.
 *
 * AgentCEO PIN-flow is mobile/tablet-first (per shareholder decision 2026-05-08):
 * standard banking pattern doesn't work well on desktop without auto-lock.
 *
 * Phase 1 (v0.2.0): hide PIN UI on desktop entirely.
 * Phase 2 (US-E1-009): unlock PIN on desktop only when auto-lock is configured
 * AND user explicitly enables it in Settings → Security.
 */
import { useEffect, useState } from 'react'

const PIN_CAPABLE_QUERY = '(max-width: 1024px) and (any-pointer: coarse)'

/** Synchronous check — false on SSR, used in initial render guard. */
export function isPinCapableDevice(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia(PIN_CAPABLE_QUERY).matches
}

/**
 * Reactive hook — re-renders if user rotates tablet, switches input mode,
 * or attaches/detaches a stylus.
 */
export function useIsPinCapable(): boolean {
  const [capable, setCapable] = useState<boolean>(() => isPinCapableDevice())

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia(PIN_CAPABLE_QUERY)
    const handler = (e: MediaQueryListEvent) => setCapable(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  return capable
}
